import { Component, OnInit } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { CrudOps } from '../../../api/constants/crud.enum'
import { Employee } from '../../../api/models/employee.model'
import { EmployeeService } from '../../../api/services/employee.service'
import { DialogService } from '../../common/services/dialog.service'
import { LoaderService } from '../../common/services/loader.service'
import { PpwLocalStorageService } from '../../common/services/ppwlocalstorage.service'
import { SnackbarService } from '../../common/services/snackbar.service'
import { notUndefined } from '../../common/utils/conditional-assert'
import { UnsubscribeOnDestroyAdapter } from '../../shared/adapter/unsubscribe-on-destroy-adapter'
import { EmployeeDialogComponent } from './employee-dialog/employee-dialog.component'

@Component({
    selector: 'ppw-employee',
    templateUrl: './employee.component.html',
    styleUrls: ['./employee.component.scss']
})
export class EmployeeComponent extends UnsubscribeOnDestroyAdapter implements OnInit {
    pageTitle = 'Employees'
    selectedEmployee?: Employee
    displayedColumns: Array<string> = ['firstName', 'lastName', 'atlassianId']
    employees$: Observable<Array<Employee> | undefined>

    private readonly prefix = 'EmployeesComponent'
    private readonly employeeSubject: BehaviorSubject<Array<Employee> | undefined> = new BehaviorSubject<
        Array<Employee> | undefined
    >(undefined)
    private latestEmployees!: Array<Employee>

    constructor(
        private readonly loaderService: LoaderService,
        private readonly employeeService: EmployeeService,
        private readonly ppwLocalStorageService: PpwLocalStorageService,
        private readonly snackbarService: SnackbarService,
        private readonly dialogService: DialogService
    ) {
        super()
        this.employees$ = this.employeeSubject.asObservable()
    }

    async ngOnInit(): Promise<void> {
        await this.fetchAllEmployees()
    }

    create(): void {
        this.showEmployeeDialog(CrudOps.Create, undefined, this.latestEmployees)
    }

    edit(): void {
        if (this.selectedEmployee) {
            this.showEmployeeDialog(CrudOps.Update, this.selectedEmployee, this.latestEmployees)
        }
    }

    archive(): void {
        if (this.selectedEmployee) {
            this.showEmployeeDialog(CrudOps.Delete, this.selectedEmployee, this.latestEmployees)
        }
    }

    select(employee: Employee): void {
        if (this.selectedEmployee !== employee) {
            this.selectedEmployee = employee
            this.ppwLocalStorageService.storeData(
                this.ppwLocalStorageService.STORAGE_KEY_SELECTED_EMPLOYEE_ID,
                this.selectedEmployee.id
            )
        } else {
            this.selectedEmployee = undefined
            this.ppwLocalStorageService.removeStoredData(
                this.ppwLocalStorageService.STORAGE_KEY_SELECTED_EMPLOYEE_ID.key
            )
        }
    }

    goToAtlassianPeople(atlassianAccountId: number): void {
        if (atlassianAccountId) {
            const url = `https://peopleware.atlassian.net/jira/people/${atlassianAccountId}`
            window.open(url)
        }
    }

    private async fetchAllEmployees(): Promise<void> {
        this.loaderService.startLoading()
        let employeeList
        try {
            employeeList = await this.employeeService.getAllEmployees()
            this.employeeSubject.next(employeeList.items)
            this.latestEmployees = employeeList.items
            const storedEmployeeId = this.ppwLocalStorageService.getStoredData(
                this.ppwLocalStorageService.STORAGE_KEY_SELECTED_EMPLOYEE_ID
            )
            const foundEmployee = employeeList.items.find((employee) => employee.id === storedEmployeeId)
            if (foundEmployee) {
                this.selectedEmployee = foundEmployee
            }
            /**
             * MUDO I have no idea what this does, investigate and delete comment if done.
             */
            // this.checkOverFlow();
        } catch (err) {
            this.snackbarService.handleError(this.prefix, err)
        } finally {
            this.loaderService.stopLoading()
        }
    }

    private showEmployeeDialog(crud: CrudOps, employee?: Employee, employees?: Array<Employee>): void {
        this.subscriptions.add(
            this.dialogService
                .open(EmployeeDialogComponent, { employee, employees, crud })
                .afterClosed()
                .subscribe((savedEmployee) => {
                    if (savedEmployee) {
                        const index = this.latestEmployees.findIndex(
                            (e) => e.id === notUndefined(this.selectedEmployee).id
                        )
                        if (crud === CrudOps.Create) {
                            this.latestEmployees.splice(0, 0, savedEmployee)
                        } else if (crud === CrudOps.Update) {
                            this.latestEmployees.splice(index, 1, savedEmployee)
                        } else if (crud === CrudOps.Delete) {
                            this.latestEmployees.splice(index, 1)
                        }
                        this.employeeSubject.next(this.latestEmployees)
                        if (crud === CrudOps.Create) {
                            this.snackbarService.promtSnackBar(
                                `${notUndefined(savedEmployee).fullName} has been created`
                            )
                        } else if (crud === CrudOps.Update) {
                            this.snackbarService.promtSnackBar(
                                `${notUndefined(this.selectedEmployee).fullName} has been updated`
                            )
                        } else if (crud === CrudOps.Delete) {
                            this.snackbarService.promtSnackBar(
                                `${notUndefined(this.selectedEmployee).fullName} has been deleted`
                            )
                        }
                        this.selectedEmployee = savedEmployee
                    }
                })
        )
    }
}
